import React, { FC, useMemo } from 'react';
import { FaBullhorn, FaCreditCard, FaInbox, FaRegFileAlt } from 'react-icons/fa';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { Button, TabItem, Tabs } from '@/lib';
import { RouteConstants } from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetCampaignsCountV2Query } from '@/services/endpoints/campaigns';
import { useGetWhatsappConversationsQuery } from '@/services/endpoints/whatsappInbox';
import { useWhatsappTemplates } from '../hooks/useWhatsappTemplates';
import { useWhatsappChannelConnector, WHATSAPP_CONNECTOR_NAME } from '@/features/connectors/whatsapp';
import { WhatsappBusinessOnboardingBlock } from './WhatsappBusinessOnboardingBlock';
import { CampaignsWhatsappRoutesV2 } from '../Whatsapp/CampaignsWhatsappRoutesV2';
import { useTranslation } from 'react-i18next';
import { Loading } from '@/components/loaders';
import { CampaignsCreditBalanceBar } from '../credits';
import { LIST_POLLING_MS, unreadConversationCount } from '@/features/whatsappInbox/inboxUtils';
import { useWhatsappInboxEnabled } from '@/features/whatsappInbox';

const WHATSAPP_CAMPAIGNS_PATTERNS = [
  RouteConstants.whatsappCampaigns,
  RouteConstants.createCampaignWhatsapp,
  RouteConstants.editCampaignWhatsapp,
  RouteConstants.whatsappCampaignDetails,
];

const WHATSAPP_TEMPLATES_PATTERNS = [
  RouteConstants.whatsappTemplates,
  RouteConstants.createWhatsappTemplate,
  RouteConstants.editWhatsappTemplate,
  RouteConstants.whatsappTemplateDetails,
];

const WHATSAPP_PACKAGES_PATTERNS = [RouteConstants.whatsappPackages];

const WHATSAPP_INBOX_PATTERNS = [RouteConstants.whatsappCampaignInbox, RouteConstants.whatsappCampaignInboxConversation];

const ConnectorNotConnectedBlock: FC<{ type: 'whatsapp' }> = ({ type }) => {
  const history = useHistory();
  const { t } = useTranslation('campaign');

  const handleConnect = () => {
    if (type === 'whatsapp') {
      history.push(RouteConstants.destinations);
    }
  };

  return (
    <div className='flex flex-col items-center justify-center gap-6 rounded-lg border border-gray-400 py-20'>
      <h2 className='text-4xl font-semibold text-slate-600'>{t('whatsappWorkspace.noWhatsappConnectorTitle', { connectorName: WHATSAPP_CONNECTOR_NAME })}</h2>
      <div className='flex flex-col items-center gap-4'>
        {t('whatsappWorkspace.noWhatsappConnectorDescription', { connectorName: WHATSAPP_CONNECTOR_NAME })}
      </div>
      <FaRegFileAlt className='size-12 text-gray-400' />
      <Button onClick={handleConnect}>{t('goToDestinationsText')}</Button>
    </div>
  );
};

const WhatsappWorkspaceInit = () => {
  const history = useHistory();
  const location = useLocation();
  const workspace = useWorkspace();
  const inboxEnabled = useWhatsappInboxEnabled();

  const { data: campaignsCount } = useGetCampaignsCountV2Query({ workspaceId: workspace.id });
  const { templatesCount, isLoading: isTemplatesLoading } = useWhatsappTemplates();
  const { data: conversations } = useGetWhatsappConversationsQuery(
    { workspaceId: workspace.id },
    { skip: !inboxEnabled, pollingInterval: LIST_POLLING_MS }
  );
  const inboxUnreadCount = useMemo(() => unreadConversationCount(conversations ?? []), [conversations]);

  const tabItems = useMemo<TabItem[]>(
    () => [
      {
        id: 'campaigns',
        title: 'campaign.whatsappWorkspace.campaigns' as const,
        icon: <FaBullhorn className='size-4' />,
        count: campaignsCount ?? 'N/A',
        element: <CampaignsWhatsappRoutesV2 />,
      },
      {
        id: 'templates',
        title: 'campaign.whatsappWorkspace.templates' as const,
        icon: <FaRegFileAlt className='size-4' />,
        count: isTemplatesLoading ? 'N/A' : templatesCount,
        element: <CampaignsWhatsappRoutesV2 />,
      },
      {
        id: 'packages',
        title: 'campaign.whatsappWorkspace.packages' as const,
        icon: <FaCreditCard className='size-4' />,
        count: 4,
        element: <CampaignsWhatsappRoutesV2 />,
      },
      ...(inboxEnabled
        ? [
            {
              id: 'inbox',
              title: 'campaign.whatsappWorkspace.inbox' as const,
              icon: <FaInbox className='size-4' />,
              count: inboxUnreadCount,
              element: <CampaignsWhatsappRoutesV2 />,
            },
          ]
        : []),
    ],
    [campaignsCount, inboxEnabled, inboxUnreadCount, isTemplatesLoading, templatesCount]
  );

  const activeTabId = useMemo(() => {
    const isCampaignRoute = WHATSAPP_CAMPAIGNS_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isCampaignRoute) {
      return 'campaigns';
    }

    const isTemplateRoute = WHATSAPP_TEMPLATES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isTemplateRoute) {
      return 'templates';
    }

    const isPackageRoute = WHATSAPP_PACKAGES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isPackageRoute) {
      return 'packages';
    }

    const isInboxRoute =
      inboxEnabled && WHATSAPP_INBOX_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isInboxRoute) {
      return 'inbox';
    }

    return 'campaigns';
  }, [inboxEnabled, location.pathname]);

  const currentTab = tabItems.find((item) => item.id === activeTabId) ?? tabItems[0];

  const handleSelectTab = (tabItem: TabItem) => {
    if (tabItem.id === 'campaigns') {
      history.push(RouteConstants.whatsappCampaigns);
      return;
    }

    if (tabItem.id === 'templates') {
      history.push(RouteConstants.whatsappTemplates);
      return;
    }

    if (tabItem.id === 'packages') {
      history.push(RouteConstants.whatsappPackages);
      return;
    }

    if (tabItem.id === 'inbox') {
      history.push(RouteConstants.whatsappCampaignInbox);
    }
  };

  return (
    <div className='grid w-full grid-cols-1 gap-4'>
      <CampaignsCreditBalanceBar channel='whatsapp' />
      <Tabs items={tabItems} currentTab={currentTab.title} onSelectTab={handleSelectTab} />
    </div>
  );
};

export const CampaignsWhatsappWorkspaceV2: FC = () => {
  const { isLoading, isAuthenticated, needsOnboarding, onboarding } = useWhatsappChannelConnector();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <ConnectorNotConnectedBlock type='whatsapp' />;
  }

  if (needsOnboarding) {
    return <WhatsappBusinessOnboardingBlock onboardingUrl={onboarding?.onboardingUrl} />;
  }

  return <WhatsappWorkspaceInit />;
};
